/**
 * HFRConnect Platform Standards — Single Source of Truth
 *
 * All design tokens for the platform live here.
 * theme.js consumes these tokens; components reference theme.palette / theme.custom.
 * AI agents and developers must use these constants — never hardcode hex values or
 * magic numbers in component files.
 *
 * Import pattern:
 *   import { TOUCH, COMPACT_TOOLBAR, EXPLORE_TOOLBAR, ZINDEX, SPACING, ADMIN_WIZARD_LAYOUT } from '@/core/platform/platformStandards';
 */

// ---------------------------------------------------------------------------
// Color Palettes
// ---------------------------------------------------------------------------

export const darkColors = {
  background: {
    main: '#0f172a',
    paper: '#1e293b',
    modal: '#0f172a',
  },
  navigation: {
    topBar: '#0f172a',
    sidebar: '#334155',
  },
  borders: {
    primary: '#475569',
    /** Muted teal hairline — aligns with primary cyan, tuned for dark paper `#1e293b` */
    subtle: '#4a807a',
    focus: '#06b6d4',
  },
  text: {
    primary: '#f8fafc',
    secondary: '#cbd5e1',
    muted: '#94a3b8',
    link: '#22d3ee',
    linkHover: '#06b6d4',
  },
  actions: {
    primary: '#06b6d4',
    primaryHover: '#0891b2',
    secondary: '#475569',
    destructive: '#ef4444',
  },
  status: {
    success: '#22c55e',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
  },
  charts: {
    primary: '#22d3ee',
    secondary: '#34d399',
    tertiary: '#fb923c',
    accent: '#a78bfa',
  },
  roles: {
    fundManager: '#22d3ee',
    allocator: '#34d399',
    admin: '#a78bfa',
  },
  forms: {
    inputBg: '#334155',
    inputBorder: '#475569',
    inputFocus: '#06b6d4',
    placeholder: '#94a3b8',
  },
  highlighter: '#FFD700',
  opportunities: {
    custom1: '#22d3ee',
    custom2: '#34d399',
    custom3: '#fb923c',
    custom4: '#a78bfa',
    custom5: '#f472b6',
    custom6: '#fbbf24',
    custom7: '#4ade80',
    custom8: '#60a5fa',
    custom9: '#f87171',
    custom10: '#c084fc',
  },
};

export const lightColors = {
  background: {
    main: '#f8f9fa',
    paper: '#ffffff',
    modal: '#ffffff',
  },
  navigation: {
    topBar: '#ffffff',
    sidebar: '#f7fafc',
  },
  borders: {
    primary: '#cbd5e0',
    /** Muted teal hairline — aligns with primary teal, tuned for white paper */
    subtle: '#a3d4cf',
    focus: '#38b2ac',
  },
  text: {
    primary: '#1a202c',
    secondary: '#4a5568',
    muted: '#718096',
    link: '#2c7a7b',
    linkHover: '#285e61',
  },
  actions: {
    primary: '#38b2ac',
    primaryHover: '#319795',
    secondary: '#e2e8f0',
    destructive: '#e53e3e',
  },
  status: {
    success: '#38a169',
    warning: '#d69e2e',
    error: '#e53e3e',
    info: '#3182ce',
  },
  charts: {
    primary: '#0369a1',
    secondary: '#2f855a',
    tertiary: '#dd6b20',
    accent: '#805ad5',
  },
  roles: {
    fundManager: '#2c7a7b',
    allocator: '#2f855a',
    admin: '#805ad5',
  },
  forms: {
    inputBg: '#ffffff',
    inputBorder: '#cbd5e0',
    inputFocus: '#38b2ac',
    placeholder: '#a0aec0',
  },
  highlighter: '#DAA520',
  opportunities: {
    custom1: '#0891b2',
    custom2: '#059669',
    custom3: '#ea580c',
    custom4: '#7c3aed',
    custom5: '#db2777',
    custom6: '#d97706',
    custom7: '#16a34a',
    custom8: '#2563eb',
    custom9: '#dc2626',
    custom10: '#9333ea',
  },
};

// ---------------------------------------------------------------------------
// Breakpoints — mirrors MUI defaults; used for custom media queries outside MUI
// ---------------------------------------------------------------------------

export const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

// ---------------------------------------------------------------------------
// Touch Targets — revised per platform-standards.md §3a (accepted 2026-05-05)
// ---------------------------------------------------------------------------

export const TOUCH = {
  tapTarget:     36,  // px — minimum tap target for all interactive elements (WCAG 2.5.8 AA)
  elementHeight: 40,  // px — standard height for buttons, inputs, rows, and data-grid rows
  navTarget:     48,  // px — bottom nav item tap area (always-visible chrome)
  iconSize:      16,  // px — icon glyph render size; theme sets MuiSvgIcon.root globally
  hitSlop:        6,  // px — invisible padding for very small elements (close buttons, tags)

  // Pre-revision aliases — kept for backward compatibility; migrate call sites to tokens above
  minSize:    44,
  comfySize:  48,
};

// ---------------------------------------------------------------------------
// Explore V2 toolbar — numeric tokens only; colors from theme.palette
// ---------------------------------------------------------------------------

export const EXPLORE_TOOLBAR = {
  /** Sort direction icons on primary-filled metric chips (same color as label via inherit) */
  sortDirectionIconPx: 22,
};

// Explore V2 — viewport clamps (dvh) and theme.spacing() multipliers for calc() offsets.

export const EXPLORE_VIEWPORT = {
  mapRowMaxDvh: 72,
  mapRowChromeSpacing: 36,
  mapBlockMinVhXs: 50,
  mapBlockMaxSpacingXs: 50,
  detailPanelMaxVhXs: 45,
  detailPanelMaxSpacingXs: 47.5,
  mapMinHeightMdSpacing: 37.5,
  detailPanelMinWidthMdSpacing: 32.5,
  detailPanelMaxWidthMdSpacing: 50,
  detailPanelMinHeightXsSpacing: 25,
  exploreTableMaxDvhXs: 68,
  exploreTableMaxDvhSm: 74,
  exploreTableChromeSpacingXs: 34,
  exploreTableChromeSpacingSm: 32,
  filterDrawerMaxVh: 80,
  filterDrawerWidthSpacing: 47.5,
};

// ---------------------------------------------------------------------------
// Spacing Scale — based on MUI spacing(1) = 8px
// ---------------------------------------------------------------------------

export const SPACING = {
  xxs: 2,
  xs: 4,
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  xxl: 24,
  xxxl: 32,
};

// ---------------------------------------------------------------------------
// Z-Index Scale
// ---------------------------------------------------------------------------

export const ZINDEX = {
  base: 0,
  raised: 1,
  /** Pin shape layer inside react-map-gl Marker (above pulse ring, below count label) */
  mapMarkerPin: 3,
  /** Count text above pin gradient inside Marker */
  mapMarkerLabel: 4,
  stickyContent: 10,   // sticky elements within page scroll (sticky headers, chart overlays)
  dropdown: 1000,
  sticky: 1100,
  appBar: 1200,
  drawer: 1300,
  modal: 1400,
  tooltip: 1500,
  snackbar: 10000,
};

// ---------------------------------------------------------------------------
// Border Radius
// ---------------------------------------------------------------------------

export const RADIUS = {
  none: 0,
  sm: 4,
  md: 8,    // MUI default — shape.borderRadius
  lg: 12,
  xl: 16,
  full: 9999,
};

// ---------------------------------------------------------------------------
// Elevation / Shadows
// ---------------------------------------------------------------------------

export const SHADOWS = {
  none: 'none',
  subtle: '0 2px 8px rgba(0,0,0,0.04)',      // resting card state
  soft: '0 2px 4px rgba(0,0,0,0.1)',          // interactive element (light mode)
  light: '0 1px 3px rgba(0,0,0,0.12)',
  card: '0 4px 12px rgba(0,0,0,0.08)',        // card hover
  medium: '0 4px 8px rgba(0,0,0,0.15)',
  elevated: '0 4px 12px rgba(0,0,0,0.15)',    // elevated surface
  modal: '0 8px 32px rgba(0,0,0,0.15)',       // floating panel (light mode)
  heavy: '0 8px 24px rgba(0,0,0,0.2)',
  darkSoft: '0 2px 4px rgba(0,0,0,0.3)',      // interactive element (dark mode)
  darkLight: '0 1px 3px rgba(0,0,0,0.3)',
  darkMedium: '0 4px 8px rgba(0,0,0,0.4)',
  darkElevated: '0 4px 12px rgba(0,0,0,0.5)', // elevated surface (dark mode)
  darkModal: '0 8px 32px rgba(0,0,0,0.6)',    // floating panel (dark mode)
};

// ---------------------------------------------------------------------------
// Transitions — mirrors MUI transition presets
// ---------------------------------------------------------------------------

export const TRANSITIONS = {
  duration: {
    shortest: 150,
    shorter: 200,
    short: 250,
    standard: 300,
    complex: 375,
    enteringScreen: 225,
    leavingScreen: 195,
  },
  easing: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  },
};

// ---------------------------------------------------------------------------
// Typography Scale — base values; full theme typography lives in theme.js
// ---------------------------------------------------------------------------

export const FONT_FAMILY = {
  sans: ['Inter', 'Roboto', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Arial', 'sans-serif'].join(','),
  mono: 'JetBrains Mono, monospace',
};

export const FONT_SIZE = {
  xs: '0.75rem',    // 12px — caption, overline, chip, tableHeader
  sm: '0.8125rem',  // 13px — financialSmall
  md: '0.875rem',   // 14px — body2, subtitle2, button, financialRegular
  base: '1rem',     // 16px — body1, subtitle1, h5
  lg: '1.125rem',   // 18px — h4
  xl: '1.25rem',    // 20px — h3
  xxl: '1.5rem',    // 24px — h2, financialLarge
  xxxl: '2rem',     // 32px — h1
};

export const FONT_WEIGHT = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

// Hex color sequences for charting libraries (Recharts, SVG) that cannot consume theme tokens.
// These are the single source of truth — import from here, never inline in component files.
export const CHART_COLORS_PALETTE = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Emerald
  '#F59E0B', // Amber
  '#8B5CF6', // Purple
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#F97316', // Orange
  '#EC4899', // Pink
  '#6366F1', // Indigo
  '#14B8A6', // Teal
  '#A855F7', // Violet
  '#EAB308', // Yellow
  '#22C55E', // Green
];

// Teal-spectrum palette for donut / allocation charts
export const CHART_COLORS_TEAL = [
  '#008080', '#20B2AA', '#48D1CC', '#40E0D0', '#7FFFD4', '#AFEEEE',
];

// Default fallback for unknown category in chart data
export const NEUTRAL_COLOR = '#757575';

// Semantic colors for chart data points (positive/negative/neutral) —
// use theme.palette.success.main / theme.palette.error.main inside MUI components instead
export const CHART_SEMANTIC = {
  positive: '#4caf50',
  negative: '#f44336',
  neutral: '#ff9800',
};

// ---------------------------------------------------------------------------
// Admin firm enrichment / curator wizard — content max widths (px)
// Use these instead of magic numbers in FirmEnrichmentWizard and steps.
// ---------------------------------------------------------------------------

export const ADMIN_WIZARD_LAYOUT = {
  /** Stepper horizontal track */
  stepperMaxPx: 800,
  /** Main scroll column */
  contentMaxPx: 900,
  /** Autocomplete + text field stacks */
  fieldColumnMaxPx: 560,
  /** Inline form rows (e.g. ticker + button) */
  formRowMaxPx: 480,
  /** Primary nav button min width on sm+ (px); full-width on xs via sx */
  navPrimaryMinWidthPx: 140,
  /** Commit action min width on sm+ (px) */
  commitPrimaryMinWidthPx: 180,
};

// Compact toolbar height for admin-only, mouse-primary data tools
// (e.g. ConstituentSelectorPage benchmark strip, IndexComparisonReportPage toolbar).
// These are intentionally below touch minimums — desktop/mouse only.
// Must NOT be used on controls accessible to investor or manager roles.
export const COMPACT_TOOLBAR = {
  height: 30,          // px — single-line admin data toolbar height
  inputPy: 0.4,        // MUI spacing units — inline input vertical padding within toolbar
  buttonPy: 0.25,      // MUI spacing units — inline button vertical padding within toolbar
  rowPy: 0.25,         // MUI spacing units — radio/checkbox vertical padding within table row
};
